import React from 'react';
import Icon from '../../../components/AppIcon';

const TreatmentComparison = ({ treatmentPlan, alternatives }) => {
  return (
    <div className="space-y-6">
      {/* Comparison Header */}
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Understanding Your Options</h4>
            <p className="text-sm text-muted-foreground">
              Your provider has prepared alternative treatment options for your consideration. Review each option carefully to make an informed decision about your care. Each alternative has different costs, timelines, and expected outcomes.
            </p>
          </div>
        </div>
      </div>
      {/* Recommended Plan */}
      <div className="bg-success/5 rounded-lg border-2 border-success p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Star" size={20} className="text-success" />
          <h3 className="text-lg font-semibold text-foreground">Recommended Treatment Plan</h3>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
            Provider Recommended
          </span>
        </div>
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-foreground mb-2">{treatmentPlan?.planName}</h4>
            <p className="text-sm text-muted-foreground mb-3">{treatmentPlan?.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
              <p className="text-xl font-bold text-foreground">${treatmentPlan?.totalCost?.toFixed(2)}</p>
              <p className="text-xs text-success mt-1">Your cost: ${treatmentPlan?.patientResponsibility?.toFixed(2)}</p>
            </div>
            <div className="bg-background rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Duration</p>
              <p className="text-xl font-bold text-foreground">{treatmentPlan?.estimatedDuration}</p>
            </div>
            <div className="bg-background rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Treatment Phases</p>
              <p className="text-xl font-bold text-foreground">{treatmentPlan?.phases?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Alternative Options */}
      {alternatives && alternatives?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="GitCompare" size={20} className="text-primary" />
            Alternative Treatment Options
          </h3>
          <div className="space-y-4">
            {alternatives?.map((alt, idx) => (
              <div key={alt?.id} className="bg-background rounded-lg border border-border p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-muted-foreground">{idx + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">{alt?.alternativeName}</h4>
                      <p className="text-sm text-muted-foreground">{alt?.description}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-card rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                    <p className="text-lg font-bold text-foreground">${alt?.estimatedCost?.toFixed(2)}</p>
                  </div>
                  <div className="bg-card rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Duration</p>
                    <p className="text-lg font-bold text-foreground">{alt?.estimatedDuration}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="ThumbsUp" size={16} className="text-success" />
                      Advantages
                    </h5>
                    <p className="text-sm text-muted-foreground">{alt?.pros}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="ThumbsDown" size={16} className="text-warning" />
                      Disadvantages
                    </h5>
                    <p className="text-sm text-muted-foreground">{alt?.cons}</p>
                  </div>
                </div>

                {alt?.outcomeProjection && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="TrendingUp" size={16} className="text-primary" />
                      Expected Outcome
                    </h5>
                    <p className="text-sm text-muted-foreground">{alt?.outcomeProjection}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Cost Comparison Chart */}
      <div className="bg-background rounded-lg border border-border p-6">
        <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="DollarSign" size={18} className="text-primary" />
          Cost Comparison
        </h4>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{treatmentPlan?.planName}</span>
                <span className="text-sm font-bold text-foreground">${treatmentPlan?.totalCost?.toFixed(2)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-success h-2 rounded-full"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>
          {alternatives?.map(alt => {
            const percentage = (alt?.estimatedCost / treatmentPlan?.totalCost) * 100;
            return (
              <div key={alt?.id} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{alt?.alternativeName}</span>
                    <span className="text-sm font-bold text-foreground">${alt?.estimatedCost?.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Decision Support */}
      <div className="bg-muted/50 rounded-lg border border-border p-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Need Help Deciding?</strong> Your provider has recommended the primary treatment plan based on your specific clinical needs and long-term oral health goals. However, you have the right to choose any of these alternatives. Use the Discussion tab to ask questions or schedule a consultation to discuss these options in detail.
        </p>
      </div>
    </div>
  );
};

export default TreatmentComparison;